import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Headphones, Moon, Sun, Brain } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BIO_KEY = "ep_biorhythm_dob";
const ICONS = {
  quick_ground: Headphones,
  focus_5:      Brain,
  deep_10:      Sun,
  moon_bright:  Moon,
  moon_release: Moon,
};

/**
 * Full meditation library carousel with an autoplay-recommend flow.
 *
 * Loads /api/meditations/library and /api/meditations/recommend (biorhythm
 * + moon-phase aware). Each track has its own 60-second base file that we
 * loop with an HTML5 audio element; a JS timer stops playback after the
 * target duration_minutes (5 min = 5 × 60s).
 *
 * A deep-link `?meditation=<slug>` in the URL auto-selects a track — used
 * from the landing-page "recommend my meditation" form.
 */
export default function MeditationLibrary() {
  const [library, setLibrary] = useState([]);
  const [recommended, setRecommended] = useState(null);
  const [activeSlug, setActiveSlug] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [elapsedS, setElapsedS] = useState(0);
  const [carouselApi, setCarouselApi] = useState(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  // Fetch library + recommendation on mount
  useEffect(() => {
    fetch(`${BACKEND}/api/meditations/library`)
      .then((r) => r.json())
      .then((d) => setLibrary(d.tracks || []))
      .catch(() => setLibrary([]));

    const dob = typeof window !== "undefined" ? localStorage.getItem(BIO_KEY) : null;
    const url = dob
      ? `${BACKEND}/api/meditations/recommend?dob=${encodeURIComponent(dob)}`
      : `${BACKEND}/api/meditations/recommend`;
    fetch(url)
      .then((r) => r.json())
      .then((d) => setRecommended(d))
      .catch(() => setRecommended(null));
  }, []);

  // ── Carousel init fix ──────────────────────────────────────────
  // Embla measures 0-width slides on first render before the async
  // library fetch resolves. When `library` first populates (or window
  // resizes), force reInit so slide widths get measured correctly.
  useEffect(() => {
    if (!carouselApi || library.length === 0) return;
    // Small rAF delay so DOM has painted the new slides
    const raf = requestAnimationFrame(() => {
      try { carouselApi.reInit(); } catch {}
    });
    return () => cancelAnimationFrame(raf);
  }, [carouselApi, library.length]);

  useEffect(() => {
    if (!carouselApi) return;
    const onResize = () => { try { carouselApi.reInit(); } catch {} };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [carouselApi]);

  // Deep-link support: /almanac?meditation=deep_10
  useEffect(() => {
    if (!library.length) return;
    const params = new URLSearchParams(window.location.search);
    const target = params.get("meditation");
    if (target && library.some((t) => t.slug === target)) {
      setActiveSlug(target);
      // Auto-scroll into view
      setTimeout(() => {
        document.querySelector('[data-testid="meditation-library"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [library]);

  const active = library.find((t) => t.slug === activeSlug) || null;

  const stop = () => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.currentTime = 0;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setPlaying(false);
    setElapsedS(0);
  };

  const play = (track) => {
    if (activeSlug !== track.slug) {
      stop();
      setActiveSlug(track.slug);
      // Give React a tick to update src, then autoplay
      setTimeout(() => start(track), 60);
      return;
    }
    start(track);
  };

  const start = (track) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    a.loop = true;
    a.play().then(() => {
      setPlaying(true);
      setElapsedS(0);
      const totalS = (track.duration_minutes || 1) * 60;
      timerRef.current = setInterval(() => {
        setElapsedS((s) => {
          const next = s + 1;
          if (next >= totalS) {
            stop();
          }
          return next;
        });
      }, 1000);
    }).catch(() => setPlaying(false));
  };

  const toggle = (track) => {
    if (activeSlug === track.slug && playing) {
      audioRef.current?.pause();
      setPlaying(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    } else {
      play(track);
    }
  };

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  return (
    <div
      className="rounded-2xl border border-[color:var(--gold)]/25 p-6 md:p-8"
      style={{ background: "linear-gradient(135deg, rgba(212,162,76,0.05), rgba(229,168,124,0.02))" }}
      data-testid="meditation-library"
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Headphones size={14} className="text-[color:var(--gold)]" />
            <span className="label-overline text-[color:var(--gold)]/85">Grounding library</span>
          </div>
          <h3 className="mt-2 font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl tracking-tight leading-tight">
            A meditation for how <span className="font-serif-italic text-[color:var(--gold)]">today</span> feels.
          </h3>
        </div>
      </div>

      {/* Recommendation strip */}
      {recommended && (
        <div
          className="mb-5 rounded-xl border border-[color:var(--gold)]/30 p-4 md:p-5 flex items-start justify-between gap-4"
          style={{ background: "rgba(212, 162, 76, 0.06)" }}
          data-testid="meditation-recommendation"
        >
          <div>
            <div className="label-overline text-[color:var(--gold)]">For you today</div>
            <div className="mt-1 font-serif-display text-[color:var(--parchment)] text-lg md:text-xl">
              {recommended.track?.title}
            </div>
            <div className="mt-1 font-serif-italic text-[color:var(--text-secondary)] text-sm leading-relaxed">
              {recommended.why}
            </div>
          </div>
          <button
            type="button"
            onClick={() => recommended.track && toggle(recommended.track)}
            data-testid="meditation-recommend-play"
            className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium text-sm"
          >
            <Play size={14} /> Play
          </button>
        </div>
      )}

      {/* Track carousel — one row on mobile, more visible on wide screens.
          Uses shadcn Embla Carousel with a forced reInit when `library`
          first populates (fixes 0-width slide measurement on first paint). */}
      <div className="relative" data-testid="meditation-track-list">
        <Carousel
          opts={{ align: "start", loop: false, dragFree: false }}
          setApi={setCarouselApi}
          className="w-full"
        >
          <CarouselContent className="-ml-3 md:-ml-4">
            {library.map((t) => {
              const Icon = ICONS[t.slug] || Headphones;
              const isActive = activeSlug === t.slug;
              const isPlaying = isActive && playing;
              const totalS = (t.duration_minutes || 1) * 60;
              const pct = isActive ? Math.min(100, (elapsedS / totalS) * 100) : 0;
              return (
                <CarouselItem
                  key={t.slug}
                  className="pl-3 md:pl-4 basis-[85%] sm:basis-1/2 lg:basis-1/3"
                >
                  <button
                    type="button"
                    onClick={() => toggle(t)}
                    data-testid={`meditation-track-${t.slug}`}
                    className={`w-full h-full text-left rounded-xl border p-4 md:p-5 relative overflow-hidden hover:border-[color:var(--gold)]/60 transition ${
                      isActive ? "border-[color:var(--gold)]" : "border-[color:var(--gold)]/20"
                    }`}
                    style={{ background: isActive ? "rgba(212,162,76,0.08)" : "rgba(10,17,40,0.4)" }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Icon size={14} className="text-[color:var(--gold)]" />
                        <span className="label-overline text-[10px]">{t.duration_minutes} min</span>
                      </div>
                      <span
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isPlaying ? "bg-[color:var(--gold)]" : "border border-[color:var(--gold)]/60"
                        }`}
                        aria-hidden
                      >
                        {isPlaying ? (
                          <Pause size={13} className="text-[color:var(--bg-base)]" />
                        ) : (
                          <Play size={13} className="text-[color:var(--gold)] ml-[2px]" />
                        )}
                      </span>
                    </div>
                    <div className="mt-3 font-serif-display text-[color:var(--parchment)] text-lg md:text-xl leading-tight">
                      {t.title}
                    </div>
                    <div className="mt-1 font-serif-italic text-[color:var(--text-secondary)] text-xs">
                      {t.tagline}
                    </div>
                    <div className="mt-2 label-overline text-[9px] text-[color:var(--gold)]/75">
                      {t.tone}
                    </div>
                    {isActive && (
                      <div
                        className="absolute left-0 right-0 bottom-0 h-[3px]"
                        style={{ background: "rgba(245, 230, 200, 0.12)" }}
                        data-testid={`meditation-progress-${t.slug}`}
                      >
                        <div
                          className="h-full"
                          style={{
                            width: `${pct}%`,
                            background: "linear-gradient(90deg, var(--gold), var(--rose-gold))",
                            transition: "width 400ms linear",
                          }}
                        />
                      </div>
                    )}
                  </button>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <CarouselPrevious
            className="hidden sm:flex -left-3 md:-left-4 bg-[color:var(--bg-deeper)]/80 border-[color:var(--gold)]/40 text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 hover:text-[color:var(--gold)]"
            data-testid="meditation-carousel-prev"
          />
          <CarouselNext
            className="hidden sm:flex -right-3 md:-right-4 bg-[color:var(--bg-deeper)]/80 border-[color:var(--gold)]/40 text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 hover:text-[color:var(--gold)]"
            data-testid="meditation-carousel-next"
          />
        </Carousel>
      </div>

      {/* Active track hint */}
      {active && (
        <div
          className="mt-5 pt-4 border-t border-[color:var(--gold)]/15 font-serif-italic text-[color:var(--text-secondary)] text-sm"
          data-testid="meditation-active-cue"
        >
          <span className="text-[color:var(--gold)]">Cue —</span> {active.cue}
        </div>
      )}

      <audio ref={audioRef} src={active?.file || ""} preload="metadata" data-testid="meditation-audio-el" />
    </div>
  );
}
